import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, Avatar, Grid, Paper, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import { UserDetailsUseBetween } from '../../shared/use-between/user-details-use-between';
import { useBetween } from 'use-between';
import { useTranslation } from '../../i18n/useTranslation';
import { IBuildingSiteDto } from '../order/interfaces/building-site.interface';
import { getBuildingSites } from '../shopping-cart/services/shopping-cart.service';
import { IBuildingSiteListViewDto } from '../order/interfaces/building-site-list-view.interface';

const UserPanel: React.FC = () => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [initials, setInitials] = useState<string>();
    const { userDetails } = useBetween(UserDetailsUseBetween);
    const { t } = useTranslation('translation');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [street, setStreet] = useState<string>('');
    const [postalCode, setPostalCode] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [contractorName, setContractorName] = useState<string>('');

    useEffect(() => {
        if (userDetails?.initials) {
            setInitials(userDetails.initials);
        } else {
            setInitials(getInitials(`${userDetails?.firstName} ${userDetails?.lastName}`));
        }
        getAndSetBuildingSites();
    }, [userDetails]);

    const getDto = (page: number) => {
        return {
            data: [],
            count: 1,
            page: page,
            searchInput: '',
            pageSize: 1,
            fetchPerUserOnly: true,
        } as IBuildingSiteDto;
    };

    const getAndSetBuildingSites = () => {
        setIsLoading(true);
        getBuildingSites(getDto(1)).then((response) => {
            if (response) {
                const dto: IBuildingSiteDto = response.data;
                const data: IBuildingSiteListViewDto[] = dto.data;
                if (data && data.length > 0) {
                    setStreet(data[0].parentStreet);
                    setPostalCode(data[0].parentPostalCode);
                    setCity(data[0].parentCity);
                    setContractorName(data[0].parentContractorName);
                }

                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        });
    };

    const getInitials = (fullName: string): string => {
        if (!fullName) {
            return '';
        }
        const parts = fullName.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                p: '2rem',
                backgroundColor: '#f5f6fa',
                pt: 10,
            }}
        >
            <Grid
                container
                spacing={4}
                alignItems="flex-start"
                justifyContent="center"
                sx={{
                    maxWidth: 1000,
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: 3,
                    p: 4,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}
            >
                <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid #821818', pb: 0.5 }}>
                            {t('BASIC_DATA')}
                        </Typography>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('NAME')}
                            </Typography>
                            <TextField
                                style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
                                value={isLoading ? '' : userDetails?.firstName}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: isLoading && <Skeleton variant="text" width={80} height={24} />,
                                }}
                                fullWidth
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('LAST_NAME')}
                            </Typography>
                            <TextField
                                style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
                                value={isLoading ? '' : userDetails?.lastName}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: isLoading && <Skeleton variant="text" width={80} height={24} />,
                                }}
                                fullWidth
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('EMAIL')}
                            </Typography>
                            <TextField
                                style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}
                                value={isLoading ? '' : userDetails?.email}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: isLoading && <Skeleton variant="text" width={80} height={24} />,
                                }}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={3}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isSmall ? 'center' : 'flex-end',
                            height: 140,
                            borderRadius: 4,
                            background: `
                    linear-gradient(
                        90deg,
                        #b3c6fa 0%,
                        #c8d4fb 40%,
                        #e2e9ff 80%
                    )
                    `,
                            p: 3,
                            maxWidth: 500,
                            margin: '0 auto',
                            mt: 4,
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: `${userDetails?.color}`,
                                color: 'white',
                                fontWeight: 'bold',
                                width: isSmall ? 70 : 90,
                                height: isSmall ? 70 : 90,
                                fontSize: isSmall ? 22 : 28,
                            }}
                        >
                            {initials}
                        </Avatar>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserPanel;
